import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Search, Plus, Filter, X, ChevronRight, User,
         AlertCircle, CheckCircle2, Loader, Shield } from 'lucide-react'
import { usePatients } from '../hooks/useSupabase'
import { useAuth } from '../lib/auth.jsx'
import styles from './PatientsPage.module.css'

const EMPTY_FORM = {
  first_name:'', middle_name:'', last_name:'', suffix:'',
  birth:'', gender:'', marital_status:'', language:'English', race:'',
  social_security:'', blood_type:'',
  address1:'', address2:'', city:'', state:'', zipcode:'', country:'',
  cell_phone:'', area_code:'', phone:'', email:'',
  emergency_contact:'', emerg_phone:'', emerg_relation:'',
  patient_type:'Mental Health', status:'Active',
}

const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY',
  'NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY']

function NewPatientModal({ onClose, onSave }) {
  const [form, setForm] = useState(EMPTY_FORM)
  const [step, setStep] = useState(0)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState({})
  const set = (field, val) => setForm(f => ({ ...f, [field]: val }))

  const validate = () => {
    const e = {}
    if (!form.first_name.trim()) e.first_name = 'Required'
    if (!form.last_name.trim())  e.last_name  = 'Required'
    if (!form.birth)             e.birth      = 'Required'
    if (!form.gender)            e.gender     = 'Required'
    if (form.email && !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Invalid email'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSave = async () => {
    if (!validate()) return
    setSaving(true)
    try { await onSave(form); onClose() }
    catch (err) { setErrors({ _global: err.message }) }
    finally { setSaving(false) }
  }

  const steps = ['Demographics', 'Contact', 'Emergency', 'Clinical']

  const Field = ({ label, field, type='text', required, options, half }) => (
    <div className={`${styles.field} ${half ? styles.fieldHalf : ''}`}>
      <label className={styles.label}>{label}{required && <span className={styles.req}>*</span>}</label>
      {options ? (
        <select className={`${styles.input} ${errors[field] ? styles.inputError : ''}`}
          value={form[field]} onChange={e => set(field, e.target.value)}>
          <option value="">Select…</option>
          {options.map(o => <option key={o.value||o} value={o.value||o}>{o.label||o}</option>)}
        </select>
      ) : (
        <input type={type} className={`${styles.input} ${errors[field] ? styles.inputError : ''}`}
          value={form[field]} onChange={e => set(field, e.target.value)} placeholder={label} />
      )}
      {errors[field] && <span className={styles.fieldError}>{errors[field]}</span>}
    </div>
  )

  return (
    <div className={styles.modalBackdrop}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}><User size={20} style={{color:'var(--color-teal)'}}/>New Patient Registration</div>
          <button className={styles.modalClose} onClick={onClose}><X size={18}/></button>
        </div>
        <div className={styles.modalSteps}>
          {steps.map((s, i) => (
            <button key={s} className={`${styles.modalStep} ${i===step?styles.modalStepActive:''} ${i<step?styles.modalStepDone:''}`}
              onClick={() => i < step && setStep(i)}>
              <span className={styles.modalStepNum}>{i < step ? <CheckCircle2 size={14}/> : i+1}</span>{s}
            </button>
          ))}
        </div>
        <div className={styles.modalBody}>
          {errors._global && <div className={styles.globalError}><AlertCircle size={14}/>{errors._global}</div>}
          {step === 0 && (
            <div className={styles.fieldGrid}>
              <Field label="First Name" field="first_name" required half />
              <Field label="Middle Name" field="middle_name" half />
              <Field label="Last Name" field="last_name" required half />
              <Field label="Suffix" field="suffix" half options={['','Jr.','Sr.','II','III','IV']} />
              <Field label="Date of Birth" field="birth" type="date" required half />
              <Field label="Gender" field="gender" required half options={[{value:'M',label:'Male'},{value:'F',label:'Female'},{value:'O',label:'Other/Non-binary'},{value:'U',label:'Unknown'}]} />
              <Field label="Marital Status" field="marital_status" half options={['Single','Married','Divorced','Widowed','Separated']} />
              <Field label="Blood Type" field="blood_type" half options={['','A+','A-','B+','B-','AB+','AB-','O+','O-','Unknown']} />
              <Field label="Race/Ethnicity" field="race" options={['White','Black/African American','Hispanic/Latino','Asian','Native American','Multiracial','Other','Prefer not to say']} />
              <Field label="Primary Language" field="language" options={['English','Spanish','French','Mandarin','Portuguese','Arabic','Other']} />
              <Field label="Social Security #" field="social_security" half />
              <Field label="Patient Type" field="patient_type" half options={['Mental Health','Substance Use','Dual Diagnosis','Telehealth Only']} />
            </div>
          )}
          {step === 1 && (
            <div className={styles.fieldGrid}>
              <Field label="Street Address" field="address1" />
              <Field label="Apt / Suite" field="address2" half />
              <Field label="Country" field="country" half required options={[{value:'US',label:'United States'},{value:'CO',label:'Colombia'},{value:'MX',label:'Mexico'},{value:'BR',label:'Brazil'},{value:'VE',label:'Venezuela'},{value:'CA',label:'Canada'},{value:'ES',label:'Spain'},{value:'FR',label:'France'}]} />
              <Field label="City" field="city" half required />
              {/* Canonical: `state` holds the subdivision for every country —
                  the US dropdown only renders for US; everywhere else the
                  department/province/región is free text (shared geo standard
                  with full subdivision lists is audit Phase 2). */}
              {form.country === 'US'
                ? <Field label="State" field="state" required half options={STATES} />
                : <Field label="State / Province / Department" field="state" required half />}
              <Field label={form.country === 'US' ? 'ZIP Code' : 'Postal Code'} field="zipcode" half required />
              <div className={styles.fieldDivider}>Contact Information</div>
              <Field label="Cell Phone" field="cell_phone" type="tel" half />
              <Field label="Home Phone" field="phone" type="tel" half />
              <Field label="Email Address" field="email" type="email" half />
            </div>
          )}
          {step === 2 && (
            <div className={styles.fieldGrid}>
              <div className={styles.sectionNote}><Shield size={14}/> Emergency contact info is confidential and only accessed in clinical emergencies.</div>
              <Field label="Emergency Contact Name" field="emergency_contact" required />
              <Field label="Emergency Contact Phone" field="emerg_phone" type="tel" half />
              <Field label="Relationship to Patient" field="emerg_relation" half options={['Spouse','Parent','Child','Sibling','Friend','Guardian','Other']} />
            </div>
          )}
          {step === 3 && (
            <div className={styles.fieldGrid}>
              <Field label="Status" field="status" options={['Active','Inactive','Waitlist','Discharged']} half />
              <Field label="Patient Type" field="patient_type" half options={['Mental Health','Substance Use','Dual Diagnosis','Telehealth Only']} />
              <div className={styles.sectionNote}><Shield size={14}/> Diagnoses, medications, and assessments are added after registration in the patient chart.</div>
            </div>
          )}
        </div>
        <div className={styles.modalFooter}>
          <div className={styles.hipaaNote}><Shield size={12}/> PHI protected under HIPAA 45 CFR §164</div>
          <div className={styles.modalActions}>
            {step > 0 && <button className={styles.btnSecondary} onClick={() => setStep(s => s-1)}>← Back</button>}
            {step < 3
              ? <button className={styles.btnPrimary} onClick={() => { if(step===0) validate(); setStep(s => s+1) }}>Next →</button>
              : <button className={styles.btnSave} onClick={handleSave} disabled={saving}>
                  {saving ? <><Loader size={15} className={styles.spin}/> Saving…</> : <><CheckCircle2 size={15}/> Register Patient</>}
                </button>
            }
          </div>
        </div>
      </div>
    </div>
  )
}

const STATUS_COLOR = {
  Active:     { background:'rgba(45,138,78,0.1)',   color:'#2D8A4E' },
  Inactive:   { background:'rgba(107,120,133,0.1)', color:'#6B7885' },
  Waitlist:   { background:'rgba(201,124,42,0.1)',  color:'#C97C2A' },
  Discharged: { background:'rgba(36,113,163,0.1)',  color:'#2471A3' },
}

export default function PatientsPage() {
  const { orgId, loading: authLoading } = useAuth()
  const { patients, loading, error, createPatient } = usePatients(authLoading ? undefined : orgId)
  const [searchParams] = useSearchParams()
  const [showModal,    setShowModal]    = useState(false)
  const [searchTerm,   setSearchTerm]   = useState(searchParams.get('search') || '')
  const [filterStatus, setFilterStatus] = useState('All')
  const [saved,        setSaved]        = useState(false)

  useEffect(() => {
    setSearchTerm(searchParams.get('search') || '')
  }, [searchParams])

  const handleSave = async (formData) => {
    await createPatient(formData)
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
  }

  const filtered = patients.filter(p => {
    const name = `${p.first_name} ${p.last_name}`.toLowerCase()
    const matchSearch = !searchTerm || name.includes(searchTerm.toLowerCase())
    const matchStatus = filterStatus === 'All' || p.status === filterStatus
    return matchSearch && matchStatus
  })

  const calcAge = dob => dob ? Math.floor((Date.now() - new Date(dob)) / 3.156e10) + 'y' : '—'

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Patients</h1>
          <p className={styles.subtitle}>{patients.length} registered · {filtered.length} shown</p>
        </div>
        <div className={styles.headerActions}>
          {saved && <div className={styles.savedToast}><CheckCircle2 size={14}/> Patient registered successfully</div>}
          <button className={styles.addBtn} onClick={() => setShowModal(true)}><Plus size={16}/> New Patient</button>
        </div>
      </div>

      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <Search size={16}/>
          <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search by name or ID…" className={styles.searchInput}/>
          {searchTerm && <button onClick={() => setSearchTerm('')} className={styles.clearSearch}><X size={14}/></button>}
        </div>
        <div className={styles.filters}>
          <Filter size={14} style={{color:'var(--color-gray-400)'}}/>
          {['All','Active','Inactive','Waitlist','Discharged'].map(s => (
            <button key={s} className={`${styles.filterChip} ${filterStatus===s?styles.filterChipActive:''}`}
              onClick={() => setFilterStatus(s)}>{s}</button>
          ))}
        </div>
      </div>

      <div className={styles.tableCard}>
        {loading ? (
          <div className={styles.tableLoading}><Loader size={24} className={styles.spin} style={{color:'var(--color-teal)'}} /><span>Loading patients from EMR…</span></div>
        ) : error ? (
          <div className={styles.tableError}><AlertCircle size={20}/>{error}<p className={styles.errorNote}>Check Supabase connection and RLS policies.</p></div>
        ) : filtered.length === 0 ? (
          <div className={styles.tableEmpty}><User size={32} style={{color:'var(--color-gray-300)'}} /><p>{searchTerm ? 'No patients match your search.' : 'No patients registered yet.'}</p><button className={styles.addBtn} onClick={() => setShowModal(true)}>Register First Patient</button></div>
        ) : (
          <>
            <div className={styles.tableHeader}>
              <span>Patient</span><span>Age / DOB</span><span>Contact</span><span>Type</span><span>Status</span><span></span>
            </div>
            {filtered.map(p => (
              <Link key={p.patient_id} to={`/app/patients/${p.patient_id}`} className={styles.tableRow}>
                <div className={styles.patientCell}>
                  <div className={styles.avatar}>{p.first_name?.[0]}{p.last_name?.[0]}</div>
                  <div>
                    <p className={styles.patientName}>{p.first_name} {p.last_name}</p>
                    <p className={styles.patientId}>PTM-{String(p.patient_id).padStart(4,'0')}</p>
                  </div>
                </div>
                <div>
                  <p className={styles.cellPrimary}>{calcAge(p.birth)}</p>
                  <p className={styles.cellSub}>{p.birth ? new Date(p.birth).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—'}</p>
                </div>
                <div>
                  <p className={styles.cellPrimary}>{p.cell_phone || p.phone || '—'}</p>
                  <p className={styles.cellSub}>{p.email || ''}</p>
                </div>
                <p className={styles.cellPrimary}>{p.patient_type || 'Mental Health'}</p>
                <span className={styles.statusChip} style={STATUS_COLOR[p.status] || STATUS_COLOR.Active}>{p.status || 'Active'}</span>
                <ChevronRight size={16} style={{color:'var(--color-gray-300)'}}/>
              </Link>
            ))}
          </>
        )}
      </div>
      {showModal && <NewPatientModal onClose={() => setShowModal(false)} onSave={handleSave}/>}
    </div>
  )
}
